<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-07-30 10:00:00
 * @Description: 聊天对话弹窗组件 - 在线客服聊天功能
 * @Features:
 *   - 实时消息发送和接收
 *   - 消息历史记录
 *   - 支持文字消息
 *   - 自动滚动到最新消息
 *   - 响应式设计
 *   - 消息状态显示
-->

<template>
  <div
    v-if="dialogVisible"
    class="chat-dialog-overlay"
    @click.self="hideDialog"
  >
    <div class="chat-dialog">
      <!-- 弹窗头部 -->

      <div class="dialog-header">
        <h3 class="dialog-title">
          {{ dialogTitle }}
        </h3>

        <button class="close-btn" @click="hideDialog">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <!-- 聊天头部信息 -->

      <div class="chat-header">
        <div class="service-info">
          <div class="service-avatar">
            {{ chatInfo.employeeName.charAt(0) }}
          </div>

          <div class="service-details">
            <div class="service-name">{{ chatInfo.employeeName }}</div>
          </div>
        </div>
      </div>

      <!-- 消息列表区域 -->
      <div class="chat-content" ref="chatContent">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-wrapper">
          <div class="loading-spinner">
            <i class="el-icon-loading"></i>
            <span>正在加载聊天记录...</span>
          </div>
        </div>

        <div class="message-list" ref="messageList">
          <!-- 欢迎消息 -->
          <div
            class="message-item system-message"
            v-if="messages.length === 0 && !loading"
          >
            <div class="message-content">
              <div class="welcome-text">
                👋 您好！欢迎咨询{{
                  chatInfo.employeeName
                }}，我们将竭诚为您服务！
              </div>
            </div>
          </div>

          <!-- 消息列表 - 使用类似chitchat的渲染方式 -->
          <div
            v-for="(message, index) in messages"
            :key="message.id || index"
            :id="'msg' + index"
            :class="[
              'message-item',
              message.isUser ? 'item_right' : 'item_left',
            ]"
          >
            <!-- 用户消息（右侧）-->
            <div v-if="message.isUser" class="user-message">
              <div class="message-wrapper">
                <div
                  :class="['message-content', 'content_right']"
                  :data-msg-type="message.msgType"
                >
                  <div class="message-text">{{ message.content }}</div>
                </div>
                <div class="user-avatar">
                  {{
                    message.fromUserName
                      ? message.fromUserName.charAt(0)
                      : userInfo.nickname
                      ? userInfo.nickname.charAt(0)
                      : "用"
                  }}
                </div>
              </div>
            </div>

            <!-- 对方消息（左侧）-->
            <div v-else class="service-message">
              <div class="message-wrapper">
                <div class="service-avatar">
                  {{
                    message.fromUserName ? message.fromUserName.charAt(0) : "客"
                  }}
                </div>
                <div
                  :class="['message-content', 'content_left']"
                  :data-msg-type="message.msgType"
                >
                  <div class="sender-name">{{ message.fromUserName }}</div>
                  <div class="message-text">{{ message.content }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 正在输入提示 -->
          <div v-if="isServiceTyping" class="typing-indicator">
            <div class="service-avatar">
              {{
                chatInfo.employeeName ? chatInfo.employeeName.charAt(0) : "客"
              }}
            </div>
            <div class="typing-content">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div class="typing-text">
                {{ chatInfo.employeeName }}正在输入...
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 消息输入区域 -->

      <div class="chat-input">
        <div class="input-container">
          <textarea
            v-model="inputMessage"
            placeholder="请输入您的问题..."
            class="message-input"
            rows="2"
            maxlength="500"
            @keydown="handleKeydown"
            ref="messageInput"
          ></textarea>

          <div class="word-limit">{{ inputMessage.length }}/500</div>
        </div>

        <div class="input-actions">
          <div class="input-tips">
            <span class="tip-text">按 Enter 发送，Shift + Enter 换行</span>
          </div>

          <button
            class="send-btn"
            :disabled="!inputMessage.trim()"
            @click="sendMessage"
          >
            发送
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";
import MessageNew from "@/utils/messageNew";

export default {
  name: "ChatDialog",
  data() {
    return {
      // 弹窗显示状态
      dialogVisible: false,
      // 输入的消息内容
      inputMessage: "",
      // 聊天记录加载状态
      loading: false,
      // 客服正在输入状态
      isServiceTyping: false,
      // 消息列表
      messages: [],
      // 当前聊天对象信息
      chatInfo: {
        employeeId: null,
        employeeName: "",
        chatType: "service", // 'service' 或 'employee'
      },
      // 自动刷新定时器
      refreshTimer: null,
      // 刷新间隔（毫秒）
      refreshInterval: 5000,
    };
  },
  computed: {
    /**
     * 用户信息
     */
    userInfo() {
      return this.$store.state.user.userInfo || {};
    },

    // 获取当前用户ID
    currentUserId() {
      return this.userInfo.id || this.userInfo.userId;
    },

    // 格式化标题显示
    dialogTitle() {
      const baseName = this.chatInfo.employeeName || "在线客服";
      return baseName;
    },
  },

  watch: {
    // 监听弹窗显示状态
    dialogVisible(newVal) {
      if (newVal) {
        // 弹窗打开时
        this.$nextTick(() => {
          this.scrollToBottom();
          this.startAutoRefresh();
        });
      } else {
        // 弹窗关闭时
        this.stopAutoRefresh();
      }
    },

    // 监听消息列表变化
    messages: {
      handler(newMessages) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
  },
  created() {
    // 监听显示聊天弹窗事件
    this.$bus.on("show-chat-dialog", this.showDialog);
  },
  beforeDestroy() {
    // 移除事件监听
    this.$bus.off("show-chat-dialog", this.showDialog);
    // 停止自动刷新定时器
    this.stopAutoRefresh();
  },
  methods: {
    /**
     * 显示聊天弹窗
     */
    showDialog(chatData = {}) {
      // 检查登录状态
      if (!this.$store.getters["user/isLoggedIn"]) {
        this.$message.warning("请先登录后再进行聊天");
        this.$bus.emit("show-login-dialog");
        return;
      }

      // 设置聊天对象信息
      this.chatInfo = {
        employeeId: chatData.employeeId || null,
        employeeName: chatData.employeeName || "在线客服",
        chatType: chatData.chatType || "service",
      };

      this.dialogVisible = true;

      // 如果有预加载的聊天历史，使用它；否则重新加载
      if (chatData.chatHistory && Array.isArray(chatData.chatHistory)) {
        console.log("使用预加载的聊天历史:", chatData.chatHistory);
        this.loadPreloadedChatHistory(chatData.chatHistory);
      } else {
        // 加载聊天历史记录
        this.loadChatHistory();
      }

      // 延迟聚焦输入框
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }
      });
    },

    /**
     * 隐藏聊天弹窗
     */
    hideDialog() {
      this.dialogVisible = false;
      this.saveMessageHistory();
    },

    /**
     * 处理键盘事件
     * @param {Event} event - 键盘事件
     */
    handleKeydown(event) {
      // Shift + Enter 换行，Enter 发送
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },

    /**
     * 发送消息
     */
    async sendMessage() {
      const message = this.inputMessage.trim();

      // 使用MessageNew工具验证消息内容
      const validation = MessageNew.validateContent(message);
      if (!validation.valid) {
        this.$message.warning(validation.errors[0]);
        return;
      }

      // 验证是否有员工ID
      if (!this.chatInfo.employeeId) {
        this.$message.error("聊天对象信息错误，请重新打开聊天窗口");
        return;
      }

      try {
        // 使用MessageNew创建消息对象
        const newMessage = MessageNew.create({
          content: message,
          employeeId: this.chatInfo.employeeId,
          createId: this.userInfo.id,
          type: "text",
        });

        // 添加用户消息到列表（立即显示，无需等待服务器响应）
        const userMessage = {
          id: Date.now() + Math.random(), // 临时ID
          content: message,
          msgType: "3",
          fromUserId: this.userInfo.id,
          fromUserName:
            this.userInfo.nickname || this.userInfo.username || "用户",
          toUserId: this.chatInfo.employeeId,
          toUserName: this.chatInfo.employeeName,
          createTime: new Date().toISOString(),
          isUser: true,
          timestamp: newMessage.timestamp,
          isRevoked: false,
          orderId: "",
          extraData: {},
        };

        this.messages.push(userMessage);

        // 限制消息数量，避免内存占用过多
        if (this.messages.length > 100) {
          this.messages.splice(0, this.messages.length - 100);
        }

        // 清空输入框
        this.inputMessage = "";

        // 滚动到底部
        this.scrollToBottom();

        // 发送消息到服务器
        await this.sendMessageToServer(newMessage);
      } catch (error) {
        console.error("发送消息失败:", error);
        this.$message.error("发送失败，请重试");
      }
    },

    /**
     * 发送消息到服务器
     * @param {Object} messageData - 消息数据对象
     */
    async sendMessageToServer(messageData) {
      try {
        // 根据接口文档构造完整的消息数据
        const completeMessageData = {
          fromUserId: this.userInfo.id,
          fromUserName:
            this.userInfo.nickname || this.userInfo.username || "用户",
          toUserId: this.chatInfo.employeeId,
          content: messageData.content,
          msgType: "3",
          orderId: "",
          readStatus: "0", // 固定为"0"（未读）
        };

        const response = await API.message.messageAdd(completeMessageData);

        if (response && response.code === 0) {
          // 消息发送成功，可以在这里处理响应
          console.log("消息发送成功:", response);
        }
      } catch (error) {
        console.error("发送消息到服务器失败:", error);
        throw error;
      }
    },

    /**
     * 加载聊天历史记录
     */
    async loadChatHistory() {
      if (!this.chatInfo.employeeId || !this.userInfo.id) {
        return;
      }

      try {
        // 根据接口文档，messageChatHistory 需要两个路径参数：fromUserId 和 toUserId
        const fromUserId = this.userInfo.id; // 当前用户ID
        const toUserId = this.chatInfo.employeeId; // 聊天对象（员工）ID

        const response = await API.message.messageChatHistory(
          fromUserId,
          toUserId
        );

        if (
          response &&
          response.code === 0 &&
          response.data &&
          Array.isArray(response.data)
        ) {
          // 清空现有消息
          this.messages = [];

          // 转换API数据格式为组件格式
          const convertedMessages = this.convertHistoryToMessages(
            response.data
          );

          // 添加转换后的消息
          this.messages = convertedMessages;

          // 滚动到底部显示最新消息
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("加载聊天历史失败:", error);
        // 如果加载历史失败，使用本地存储的消息
        this.loadMessageHistory();
      }
    },

    /**
     * 加载预加载的聊天历史记录
     * @param {Array} chatHistory - 预加载的聊天历史数据
     */
    loadPreloadedChatHistory(chatHistory) {
      try {
        // 清空现有消息
        this.messages = [];

        if (chatHistory.length === 0) {
          console.log("没有聊天历史记录");
          return;
        }

        // 转换预加载的历史记录为组件格式
        const convertedMessages = this.convertHistoryToMessages(chatHistory);

        // 添加转换后的消息
        this.messages = convertedMessages;

        console.log(
          "预加载聊天历史记录成功，共",
          this.messages.length,
          "条消息"
        );

        // 滚动到底部显示最新消息
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("加载预加载聊天历史失败:", error);
        // 如果预加载失败，回退到重新加载
        this.loadChatHistory();
      }
    },

    /**
     * 转换历史记录数据为消息组件格式
     * @param {Array} historyData - 历史记录数据
     * @returns {Array} 转换后的消息数组
     */
    convertHistoryToMessages(historyData) {
      const currentUserId = this.userInfo?.id || this.userInfo?.userId;

      return historyData.map((item) => ({
        id: item.id || Date.now() + Math.random(),
        content: item.content || "",
        msgType: this.convertMsgType(item.msgType),
        fromUserId: item.fromUserId,
        fromUserName: item.fromUserName,
        toUserId: item.toUserId,
        toUserName: item.toUserName,
        createTime: item.createTime,
        isUser: item.fromUserId === currentUserId,
        timestamp: this.formatTimestamp(item.createTime),
        isRevoked: item.isRevoked || false,
        orderId: item.orderId,
        extraData: item.extraData || {},
      }));
    },

    /**
     * 转换消息类型
     * @param {string} msgType - 原始消息类型
     * @returns {string} 转换后的消息类型
     */
    convertMsgType(msgType) {
      // 根据实际接口返回的msgType映射
      const typeMap = {
        1: "text", // 文本消息
        2: "image", // 图片消息
        3: "text", // 普通文本（看起来接口返回的是'3'）
        text: "text",
        image: "image",
        order: "order",
        system: "system",
      };

      return typeMap[msgType] || "text";
    },

    /**
     * 格式化时间戳
     * @param {string} createTime - 创建时间
     * @returns {string} 格式化后的时间
     */
    formatTimestamp(createTime) {
      if (!createTime) return "";

      try {
        const date = new Date(createTime);
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffMinutes = Math.floor(diffTime / (1000 * 60));

        if (diffMinutes < 1) {
          return "刚刚";
        } else if (diffMinutes < 60) {
          return `${diffMinutes}分钟前`;
        } else {
          return date.toLocaleTimeString("zh-CN", {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } catch (error) {
        console.warn("时间格式化失败:", error);
        return createTime;
      }
    },

    /**
     * 滚动到消息列表底部
     */
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContent = this.$refs.chatContent;
        if (chatContent) {
          chatContent.scrollTop = chatContent.scrollHeight;
        }
      });
    },

    /**
     * 保存消息历史到本地存储
     */
    saveMessageHistory() {
      try {
        const historyKey = "chat_message_history";
        const history = {
          messages: this.messages,
          timestamp: Date.now(),
        };
        localStorage.setItem(historyKey, JSON.stringify(history));
      } catch (error) {
        // console.error('保存聊天记录失败:', error)
      }
    },

    /**
     * 加载消息历史记录
     */
    loadMessageHistory() {
      try {
        const historyKey = "chat_message_history";
        const historyData = localStorage.getItem(historyKey);

        if (historyData) {
          const history = JSON.parse(historyData);
          const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

          // 只加载24小时内的消息
          if (history.timestamp > oneDayAgo) {
            this.messages = history.messages || [];
          }
        }
      } catch (error) {
        // console.error('加载聊天记录失败:', error)
      }
    },

    /**
     * 开始自动刷新
     */
    startAutoRefresh() {
      this.stopAutoRefresh(); // 先清除之前的定时器

      this.refreshTimer = setInterval(() => {
        this.refreshChatHistory();
      }, this.refreshInterval);
    },

    /**
     * 停止自动刷新
     */
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    /**
     * 刷新聊天记录（增量更新，无痕刷新）
     */
    async refreshChatHistory() {
      if (!this.dialogVisible || !this.chatInfo.employeeId) return;

      try {
        // 传入 false 参数禁用loading显示
        const response = await API.message.messageChatHistory(
          this.currentUserId,
          this.chatInfo.employeeId,
          false // 不显示loading
        );

        if (response && response.code === 0 && response.data) {
          const newMessages = this.convertHistoryToMessages(response.data);

          // 如果是首次加载或没有现有消息，直接设置
          if (this.messages.length === 0) {
            this.messages = newMessages;
            this.$nextTick(() => {
              this.scrollToBottom();
            });
            return;
          }

          // 增量更新：比较消息数量和最新消息时间
          if (newMessages.length > this.messages.length) {
            // 获取当前最后一条消息的时间
            const lastExistingMessage = this.messages[this.messages.length - 1];
            const lastExistingTime = new Date(
              lastExistingMessage.createTime || lastExistingMessage.timestamp
            );

            // 找出比最后一条消息更新的消息
            const newMessagesToAdd = newMessages.filter((msg) => {
              const msgTime = new Date(msg.createTime || msg.timestamp);
              return msgTime > lastExistingTime;
            });

            if (newMessagesToAdd.length > 0) {
              // 判断用户是否在底部
              const chatContent = this.$refs.chatContent;
              const wasAtBottom = chatContent
                ? chatContent.scrollTop + chatContent.clientHeight >=
                  chatContent.scrollHeight - 5
                : true;

              // 将新消息添加到现有消息列表
              this.messages.push(...newMessagesToAdd);

              // 限制消息数量，避免内存占用过多
              if (this.messages.length > 100) {
                const removeCount = this.messages.length - 100;
                this.messages.splice(0, removeCount);
              }

              // 只有在用户本来就在底部时才自动滚动到新消息
              if (wasAtBottom) {
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
              }

              console.log(`增量更新：新增 ${newMessagesToAdd.length} 条消息`);
            }
          } else if (newMessages.length < this.messages.length) {
            // 如果服务器消息数量少于本地消息，可能有消息被删除，需要重新同步
            console.log("检测到消息数量减少，重新同步消息列表");

            // 保存滚动位置
            const chatContent = this.$refs.chatContent;
            const wasAtBottom = chatContent
              ? chatContent.scrollTop + chatContent.clientHeight >=
                chatContent.scrollHeight - 5
              : true;

            this.messages = newMessages;

            // 如果用户在底部，保持在底部
            if (wasAtBottom) {
              this.$nextTick(() => {
                this.scrollToBottom();
              });
            }
          }
        }
      } catch (error) {
        console.error("刷新聊天记录失败:", error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

/* 聊天弹窗遮罩层 */
.chat-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 聊天弹窗 */
.chat-dialog {
  width: 500px;
  height: 600px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 弹窗头部 */
.dialog-header {
  background: linear-gradient(135deg, $primary-red, lighten($primary-red, 10%));
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  .dialog-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

/* 聊天头部 */
.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  flex-shrink: 0;

  .service-info {
    display: flex;
    align-items: center;
    gap: 12px;

    .service-avatar {
      width: 32px;
      height: 32px;
      background: $primary-red;
      color: white;
      font-weight: 600;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .service-details {
      .service-name {
        font-weight: 600;
        color: #333;
      }
    }
  }
}

/* 消息内容区域 */
.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px 16px 16px;
  background: #f8f9fa;

  /* 优化滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;

    &:hover {
      background: #ccc;
    }
  }
}

.message-list {
  .message-item {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 8px;
    }
  }

  /* 系统消息 */
  .system-message {
    text-align: center;
    margin: 20px 0;

    .message-content {
      display: inline-block;
      background: #e8f4f8;
      color: #666;
      padding: 12px 16px;
      border-radius: 16px;
      font-size: 14px;

      .welcome-text {
        line-height: 1.4;
      }
    }
  }

  /* 用户消息 */
  .user-message {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 4px;

    .message-wrapper {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      max-width: 75%;
    }

    .message-content {
      background: $primary-red;
      color: white;
      padding: 12px 16px;
      border-radius: 18px 18px 4px 18px;
      word-wrap: break-word;
      line-height: 1.4;
      font-size: 14px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

      .message-text {
        margin-bottom: 0;
      }
    }

    .user-avatar {
      width: 32px;
      height: 32px;
      background: #e74c3c;
      color: white;
      font-weight: 600;
      flex-shrink: 0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
  }

  /* 客服消息 */
  .service-message {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 4px;

    .message-wrapper {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      max-width: 75%;
    }

    .service-avatar {
      width: 32px;
      height: 32px;
      background: $primary-red;
      color: white;
      font-weight: 600;
      flex-shrink: 0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      margin-top: 2px;
    }

    .message-content {
      background: white;
      color: #333;
      padding: 12px 16px;
      border-radius: 18px 18px 18px 4px;
      border: 1px solid #e8e8e8;
      word-wrap: break-word;
      line-height: 1.4;
      font-size: 14px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      .sender-name {
        font-size: 12px;
        color: #666;
        margin-bottom: 4px;
        font-weight: 500;
      }

      .message-text {
        white-space: pre-line;
        margin: 0;
      }
    }
  }
}

/* 正在输入指示器 */
.typing-indicator {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 20px;

  .service-avatar {
    width: 32px;
    height: 32px;
    background: $primary-red;
    color: white;
    font-weight: 600;
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .typing-content {
    .typing-dots {
      background: white;
      border: 1px solid #e8e8e8;
      border-radius: 18px 18px 18px 4px;
      padding: 12px 16px;
      display: flex;
      gap: 4px;
      align-items: center;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      span {
        width: 6px;
        height: 6px;
        background: #ccc;
        border-radius: 50%;
        animation: typing 1.4s infinite;

        &:nth-child(2) {
          animation-delay: 0.2s;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
        }
      }
    }

    .typing-text {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      margin-left: 2px;
    }
  }
}

/* 消息输入区域 */
.chat-input {
  border-top: 1px solid #f0f0f0;
  background: white;
  flex-shrink: 0;

  .input-container {
    padding: 16px 20px 8px;
    position: relative;

    .message-input {
      width: 100%;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 8px 12px;
      font-size: 14px;
      font-family: inherit;
      resize: none;
      outline: none;

      &:focus {
        border-color: $primary-red;
      }
    }

    .word-limit {
      position: absolute;
      bottom: 12px;
      right: 24px;
      font-size: 12px;
      color: #999;
    }
  }

  .input-actions {
    padding: 0 20px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .input-tips {
      .tip-text {
        font-size: 12px;
        color: #999;
      }
    }

    .send-btn {
      background: $primary-red;
      border: none;
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.3s ease;

      &:hover:not(:disabled) {
        background: $primary-red-dark;
      }

      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
  }
}

/* 动画效果 */
@keyframes typing {
  0%,
  60%,
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
  30% {
    transform: scale(1.2);
    opacity: 1;
  }
}
</style>
